import { addDays, parseISO, format, differenceInCalendarDays } from 'date-fns'
import config from 'isomorphic-config'
import { HotelsFill, FlightsFill, CarsFill } from '@pcln/brand'
import { Cars, Flights, Hotels } from 'pcln-icons'
import analytics from '@/shared-utils/analytics'
import { ANALYTICS_CATEGORY_HOME } from '@/constants/analytics'
import buildGA4ObjectForPostBookingXSell, {
  BuildGA4ObjectForPostBookingXSellInput
} from '@/shared-utils/ga4/post-booking-xsell'
import {
  CrossSellContentBlock,
  Booking,
  CarInventoryParams,
  HotelInventoryParams,
  Product,
  ProductIconType,
  Listing,
  ProductType
} from './types'

function getBaseServiceOptions(
  operationName: string,
  apiMetadata?: ApiMetadata
) {
  const serviceOptions = {
    url: `${config.client['pcln-graph'].url}?gqlOp=${operationName}`,
    method: 'POST',
    timeout: config.client['pcln-graph'].timeout,
    headers: {
      'apollographql-client-name': apiMetadata?.appName ?? 'next-landing',
      'apollographql-client-version': apiMetadata?.appVersion ?? '0.0.0.0'
    }
  }
  return serviceOptions
}

export function buildContent(booking: Booking): CrossSellContentBlock {
  const startDate = parseISO(booking.travelStartDateTime)
  const endDate = parseISO(booking.travelEndDateTime)
  const formattedDate = `${format(startDate, 'EEE, MMM dd')} - ${format(
    endDate,
    'EEE, MMM dd'
  )}`

  const hotelsXSellUrl = (destination: string, start: Date, end: Date) =>
    `/relax/in/${destination}/from/${format(start, 'yyyyMMdd')}/to/${format(
      end,
      'yyyyMMdd'
    )}/rooms/1`

  switch (booking.productId) {
    case Product.Hotel: {
      const checkInDate = parseISO(booking.utcCheckInDateTime)

      return {
        product: 'hotel',
        xSellMessaging: 'Your hotel booking unlocks up to 40% off rental cars',
        xSellTarget: 'Rental Cars',
        xSellTargetId: Product.Car,
        date: formattedDate,
        destination: booking.destinationCity,
        offerToken: booking.offerToken,
        headline: booking.destinationCity,
        subHeadline: `Check-in on ${format(checkInDate, 'EEE, MMM dd')}`,
        xSellUrl: `/drive/search/r/${booking.destinationCity},${
          booking.destinationCountryCode
        }/${booking.destinationCity},${booking.destinationCountryCode}/${format(
          startDate,
          'yyyyMMdd'
        )}-12:00/${format(endDate, 'yyyyMMdd')}-12:00/list`
      }
    }
    case Product.Flight: {
      const destination =
        booking.destinationCity || booking.destinationLocationCode

      const isSameDay = differenceInCalendarDays(endDate, startDate) < 1
      return {
        product: 'flight',
        xSellMessaging:
          'Your flight booking unlocks VIP hotel deals of up to 40% off',
        xSellTarget: 'Hotels',
        xSellTargetId: Product.Hotel,
        offerToken: booking.offerToken,
        date: formattedDate,
        destination,
        headline: `${booking.originLocationCode} - ${booking.destinationLocationCode}`,
        subHeadline: `Departs ${format(startDate, "EEE, MMM dd 'at' h:mm a")}`,
        xSellUrl: hotelsXSellUrl(
          destination,
          startDate,
          isSameDay ? addDays(endDate, 1) : endDate
        )
      }
    }
    case Product.Car: {
      const checkInDate = parseISO(booking.utcCheckInDateTime)

      return {
        product: 'car',
        xSellMessaging:
          'Your rental car booking unlocks VIP hotel deals of up to 40% off',
        xSellTarget: 'Hotels',
        xSellTargetId: Product.Hotel,
        offerToken: booking.offerToken,
        date: formattedDate,
        destination: booking.destinationCity,
        headline: `${booking.destinationCity}`,
        subHeadline: `Pickup on ${format(checkInDate, 'EEE, MMM do')}`,
        xSellUrl: hotelsXSellUrl(booking.destinationCity, startDate, endDate)
      }
    }
    // no default
  }
}

type ApiMetadata = {
  appName: string
  appVersion: string
}

export function buildRCInventoryRequest(
  {
    pickupDateTime,
    returnDateTime,
    pickupLocation,
    returnLocation,
    offerToken,
    tokenType
  }: CarInventoryParams,
  apiMetadata?: ApiMetadata
) {
  return {
    ...getBaseServiceOptions('rcXsell', apiMetadata),
    data: {
      query: `{
        rcXsell(
          pickupDateTime: "${pickupDateTime}",
          returnDateTime: "${returnDateTime}",
          pickupLocation: "${pickupLocation}",
          returnLocation: "${returnLocation}",
          offerToken: "${offerToken}",
          tokenType: "${tokenType}"
        ) {
          listings: content {
            id
            checkoutUrl
            carExample
            isCarExampleExact
            pricing {
              price
              currencySymbol
              disclaimer
              priceDescription
            }
            partner {
              name
              url
            }
            options
            bannerType
            carImageUrl
            vehicleFeatures {
              bagCapacity
              isAirConditioned
              isUnlimitedMileage
              mileageDistance
              mileageUnit
              peopleCapacity
            }
            rate {
              currencyCode
              currencySymbol
              dailyPrice
              isFreeCancellation
              isPrepay
              savingsPriceTotal
              strikeThroughPrice
              totalPrice
            }
          }
        }
      }`
    }
  }
}

export function buildHotelInventoryRequest(
  {
    cityId,
    cityName,
    checkIn,
    checkOut,
    cguid,
    appc,
    rid,
    productId,
    pageSize = 3,
    dealTypes
  }: HotelInventoryParams,
  apiMetadata?: ApiMetadata
) {
  return {
    ...getBaseServiceOptions('integratedListings', apiMetadata),
    data: {
      variables: {
        cityId,
        cityName,
        checkIn,
        checkOut,
        cguid,
        appc,
        rid,
        productId,
        pageSize,
        dealTypes
      },
      query: `{
        integratedListings(
          cityId: ${cityId}
          cityName: "${cityName}"
          checkIn: ${checkIn}
          checkOut: ${checkOut}
          appc: "${appc}"
          cguid: "${cguid}"
          rid: "${rid}"
          productId: ${productId}
          pageSize: ${pageSize}
        ) {
          listings {
            name
            location
            imageUrl
            guestRating
            guestReviewCount
            starRating
            savingPercentage
            originalPrice
            currentPrice
            currency
            currencySymbol
            hotelId
            href
          }
        }
      }`
    }
  }
}

export function buildItineraryDetailsRequest(
  offerToken: string,
  tokenType: string,
  apiMetadata?: ApiMetadata
) {
  return {
    ...getBaseServiceOptions('itineraryDetails', apiMetadata),
    data: {
      query: `{
        itineraryDetails(offerToken: "${offerToken}", tokenType: "${tokenType}") {
          hotelXsellCarousel {
            checkIn
            checkOut
            cityId
            cityName
          }
          rcXsellRequestParams {
            pickupDateTime
            returnDateTime
            pickupLocation
            returnLocation
          }
        }
      }`
    }
  }
}

export function trackCrossSellClicks({
  listing,
  offerId,
  parentProductId,
  xSellProductId,
  destination
}: {
  listing: Listing
  offerId: string
  parentProductId: ProductType
  xSellProductId: ProductType
  destination: string
}) {
  analytics.fireEvent({
    action: 'Feature Box CTA',
    category: ANALYTICS_CATEGORY_HOME,
    label: 'Cross Sell Choose Button'
  })

  analytics.fireGA4Event(
    buildGA4ObjectForPostBookingXSell({
      event: 'select_promotion_bundle',
      product: parentProductId,
      xSellProduct: xSellProductId,
      offerId,
      destination,
      listings: [listing]
    })
  )
}

export function trackNoInventoryXsellClick(
  props: BuildGA4ObjectForPostBookingXSellInput
) {
  analytics.fireEvent({
    action: 'Feature Box CTA',
    category: ANALYTICS_CATEGORY_HOME,
    label: 'Cross Sell Search Button'
  })

  analytics.fireGA4Event(buildGA4ObjectForPostBookingXSell(props))
}

export function firePennyButtonAnalytics() {
  analytics.fireEvent({
    action: 'penny trigger',
    category: ANALYTICS_CATEGORY_HOME,
    label: 'upcoming trip'
  })
}

/**
 * This is needed because of the following scenarios
 *
 * view more button with inventory - example text is "view more rental car deals" - product is singular and lowercase
 * search button with no inventory - example text is "Search Rental Cars" - product is upper case and plural
 */

export const X_SELL_PRODUCTS = {
  'Rental Cars': 'rental car',
  Hotels: 'hotel',
  Flights: 'flight'
} as const

export const trackViewTripClick = () => {
  analytics.fireEvent({
    action: 'Feature Box Link',
    category: ANALYTICS_CATEGORY_HOME,
    label: 'Cross Sell View Trip'
  })
}

export const getProductIconFilled = (product: string): ProductIconType => {
  switch (product) {
    case 'flight':
    case 'Flights':
      return FlightsFill
    case 'car':
    case 'Rental Cars':
      return CarsFill
    default:
      return HotelsFill
  }
}

export const getProductIcon = (product: string): ProductIconType => {
  switch (product) {
    case 'flight':
    case 'Flights':
      return Flights
    case 'car':
    case 'Rental Cars':
      return Cars
    default:
      return Hotels
  }
}
